import React from 'react';

import { type CellContext } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { ALL_POINTS_OPTION } from 'src/features/memberships/utils/constants';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from 'src/registry/ui/Tooltip';

import type { SubParking } from 'src/features/parkingManagement/types/parking';

interface CellAllowedSubParkingsProps<TData, TValue> extends CellContext<TData, TValue> {
    subParkings?: SubParking[];
    isLoadingSubParkings?: boolean;
}

function CellAllowedSubParkings<TData, TValue extends string[]>({
    cell,
    subParkings,
    isLoadingSubParkings,
}: CellAllowedSubParkingsProps<TData, TValue>) {
    if (isLoadingSubParkings) return <Skeleton className="h-4 w-24" />;

    const subParkingIds: string[] = cell.getValue();
    const { formatMessage } = useIntl();

    const containsAllSubParkings = React.useMemo(() => {
        if (!subParkingIds || !subParkings) return false;
        return subParkings
            .map((subParking) => subParking.id)
            .every((subParkingId) => subParkingIds.includes(subParkingId));
    }, [subParkingIds, subParkings]);

    const values = subParkingIds.map((subParkingId) => {
        const fondSubParking = subParkings?.find((subParking) => subParking.id === subParkingId);
        const isAllSubParkings = subParkingId === ALL_POINTS_OPTION;
        return isAllSubParkings
            ? formatMessage(membershipMessages.allSubParkings)
            : fondSubParking?.name || subParkingId;
    });

    return (
        <Tooltip>
            <TooltipProvider>
                <TooltipTrigger className="max-w-32 truncate">
                    {containsAllSubParkings ? formatMessage(membershipMessages.allSubParkings) : values.join(', ')}
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-wrap">
                    {containsAllSubParkings ? formatMessage(membershipMessages.allSubParkings) : values.join(', ')}
                </TooltipContent>
            </TooltipProvider>
        </Tooltip>
    );
}

export { CellAllowedSubParkings };
